import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

interface CommunityWebPostThumbnailProps {
  imageUrl?: string | null;
  logoUrl?: string | null;
  /** Resize/CDN-rewrite hook for image URLs. Defaults to the raw URL. */
  transformImageUrl?: (url: string, width: number) => string;
  imageWidth?: number;
  logoWidth?: number;
  className?: string;
}

export const CommunityWebPostThumbnail: FC<CommunityWebPostThumbnailProps> = ({
  imageUrl,
  logoUrl,
  transformImageUrl = (url) => url,
  imageWidth = 640,
  logoWidth = 400,
  className,
}) => {
  const [imageError, setImageError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const thumbnailRef = useRef<HTMLImageElement | null>(null);

  // A failed URL must not poison its replacement — the publish step swaps
  // image_url live while the preview stays mounted.
  const [prevImageUrl, setPrevImageUrl] = useState(imageUrl);
  if (prevImageUrl !== imageUrl) {
    setPrevImageUrl(imageUrl);
    setImageError(false);
  }
  const [prevLogoUrl, setPrevLogoUrl] = useState(logoUrl);
  if (prevLogoUrl !== logoUrl) {
    setPrevLogoUrl(logoUrl);
    setLogoError(false);
  }

  const showImage = Boolean(imageUrl) && !imageError;
  const showLogoTile = !showImage && Boolean(logoUrl) && !logoError;

  useEffect(() => {
    // SSR: the browser can start (and fail) loading the image before React hydrates, so the
    // error event never fires for onError to catch — check the element's load state directly after mount
    const img = thumbnailRef.current;
    if (img?.complete && img.naturalWidth === 0) {
      setImageError(true);
    }
  }, []);

  return (
    <div className={cn('overflow-hidden', className, { 'bg-black/5': showLogoTile })}>
      {showImage ? (
        <img
          ref={thumbnailRef}
          src={transformImageUrl(imageUrl ?? '', imageWidth)}
          alt=""
          className="size-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : showLogoTile ? (
        <img
          src={transformImageUrl(logoUrl ?? '', logoWidth)}
          alt=""
          className="size-full object-contain p-1.5"
          onError={() => setLogoError(true)}
        />
      ) : (
        <div data-testid="post-embed-gradient" className="size-full bg-gradient-to-br from-[#1C1145] to-[#7856FF]" />
      )}
    </div>
  );
};
